// MA-257 — narration pacing knob (ONE audio home, ADR-056 D6 extension).
//
// Provider-agnostic pacing constants + sentence splitting, so a future
// MA-186 pregenerated/real-TTS NarrationSource can honor the SAME pacing
// decisions WebSpeechSource applies today (src/engine/webSpeechSource.ts)
// without depending on WebSpeechSource's own utterance-chaining code.

/** Speech rate tuned for a calmer read-aloud pace for a 4-7yo listener.
 *  Was 0.9 (duplicated across 3 reader call sites, unify-only, no actual
 *  pacing change); lowered here to actually deliver "slower, calmer" per
 *  the MA-257 ask rather than just relocating the same literal. */
export const DEFAULT_NARRATION_RATE = 0.85;

/** MA-277 — resolves a device-settings `narrationRate` (see
 *  src/lib/deviceSettings.ts's NARRATION_RATE_OPTIONS) to the rate the
 *  reader should actually speak at. `null`/`undefined`/anything outside the
 *  two settings-row options falls back to DEFAULT_NARRATION_RATE, so an
 *  untouched device (or corrupt storage) never regresses from today's
 *  calmer pace. Takes a plain number, not the DeviceSettings type, so this
 *  file stays dependency-free. */
export function effectiveNarrationRate(rate: number | null | undefined): number {
  return rate === 0.75 || rate === 0.9 ? rate : DEFAULT_NARRATION_RATE;
}

/** Pause inserted between sentence segments when a page is narrated with
 *  NarrationPlayOptions.pacedPauses set. Page narration only — a single
 *  word/short phrase (tap-a-word, drill prompts) has nothing to pause
 *  between and doesn't opt in. */
export const SENTENCE_PAUSE_MS = 300;

// A small, deliberately non-exhaustive whitelist of title/honorific
// abbreviations common in early-reader story prose (verified against real
// shipped content, e.g. public/stories/0019_the-cat-in-the-ghat.json's
// "Mr. Tiger"). Without this guard, treating every "." as a sentence
// boundary would insert an audible pause mid-name ("Mr." | "Tiger said
// hello") — cutting a character's name in half on every abbreviation. v0
// scope: a fixed title/honorific list, not a general NLP tokenizer.
const PAUSE_SAFE_ABBREVIATIONS = ["Mr", "Mrs", "Ms", "Dr", "St", "Jr", "Sr", "Prof", "Capt", "Gen", "Sgt", "Rev"];

/** Split narration text into sentence-ish segments for inter-sentence
 *  pausing. Abbreviation-aware (see PAUSE_SAFE_ABBREVIATIONS above); keeps
 *  terminal punctuation (and one trailing closing quote, straight or
 *  curly) on the preceding segment. Always returns at least one segment
 *  (falls back to the whole trimmed text when no boundary is found) so a
 *  caller never has to special-case "no punctuation" text. */
export function splitIntoSentences(text: string): string[] {
  const abbrevAlt = PAUSE_SAFE_ABBREVIATIONS.join("|");
  const boundary = new RegExp(`(?<!\\b(?:${abbrevAlt}))([.!?]+(?:["'”’])?)(?:\\s+|$)`, "g");
  const segments: string[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = boundary.exec(text)) !== null) {
    const end = m.index + m[1].length;
    const segment = text.slice(last, end).trim();
    if (segment) segments.push(segment);
    last = m.index + m[0].length;
    if (m[0].length === 0) boundary.lastIndex += 1; // guard a zero-width match from looping forever
  }
  const rest = text.slice(last).trim();
  if (rest) segments.push(rest);
  return segments.length > 0 ? segments : [text];
}
